/**
 * src/components/primitives/EntitlementGate.tsx — consumer-entitlement gate
 * primitive (FR-029).
 *
 * MIRROR of `AccessGate.tsx`'s three-state, disable-and-tooltip,
 * server-is-real-gate contract — but consumes the CONSUMER-identity plane
 * (`audienceId` issued by the consumer-session HTTP boundary; resolved
 * server-side via `api.consumerIdentity.resolveConsumerSession`) and the
 * BROKER's decision (`{ entitled, reason }` shaped per
 * `convex/broker.ts:getEntitlement`), NOT the operator member role.
 *
 * The three documented states (mirror of `AccessGate.tsx` lines 11-34):
 *
 *   1) RESOLVING — either the consumer-session resolver is still loading
 *      (`useQuery(api.consumerIdentity.resolveConsumerSession, ...) ===
 *      undefined`) OR the caller has not yet supplied a `decision` prop
 *      (`decision === undefined`). Render a `<Skeleton>` shaped like a
 *      gated affordance; NEVER render the underlying children in either
 *      enabled or disabled form. Rendering either optimistically lies
 *      about the audience's entitlement during the few-hundred-ms window
 *      before the broker's read completes.
 *
 *   2) ENTITLED (`decision.entitled === true`) — render the children
 *      verbatim, no wrapper-imposed visual change, no `pointer-events:
 *      none`. The server is still the real gate — every gated media
 *      surface re-checks server-side (CloudFront signer / private-feed
 *      token issuance / audio enclosure signer). INV-1: the broker
 *      signs nothing; this primitive only decides UX.
 *
 *   3) DENIED — every other branch: no consumer session
 *      (`resolveConsumerSession` returned `null`), an unrecognised
 *      `audienceId`, a `decision.entitled === false`, or an explicit
 *      `decision === null`. Render the children inside a wrapper that:
 *        - keeps the children in the DOM (DISABLE-not-HIDE — the audience
 *          sees the affordance exists, just behind the paywall);
 *        - carries an accessible tooltip (`title` + `aria-label` +
 *          `aria-describedby` → an off-screen description) naming the
 *          required feature so power-users can cross-reference the
 *          subscription plan;
 *        - applies the disable visual contract (opacity, cursor:
 *          not-allowed, pointer-events: none) so real browsers block
 *          the click even though jsdom may still synthesise an event.
 *
 * Honest-state contract (CLAUDE.md §13 inherited from circa-vod2live-port-v1f):
 *   - `decision === undefined`        → RESOLVING (Skeleton)
 *   - `decision === null`             → DENIED   (no broker answer yet → fail-closed)
 *   - `decision.entitled === false`   → DENIED   (broker said deny, e.g. `no_subscription`)
 *   - `decision.entitled === true`    → ENTITLED (broker said allow)
 *
 * The `null` branch is the canonical fail-closed posture (PRD INV-4): when
 * the broker decision is unreachable (no public wrapper query landed yet,
 * or no consumer session), the gate denies; it NEVER renders the gated
 * affordance optimistically. A real backend error from the consumer
 * session query bubbles up as an exception (caller is responsible for
 * the surface-level toast — the gate itself does not catch).
 *
 * BOUNDARY OBSERVED (PRD FR-028 / CLAUDE.md §10):
 *   This primitive does NOT consume `useCurrentMember()` or any operator
 *   identity surface. It does NOT call `requireAuth`/`requireAdmin`/
 *   `requirePermission`. The `audienceId` is the opaque app-issued key,
 *   never the Stripe customer id and never the operator member id.
 *
 * @see src/components/primitives/AccessGate.tsx — the operator-role gate
 *      whose three-state pattern this file mirrors (BUT does not edit).
 * @see convex/consumerIdentity.ts — `resolveConsumerSession` (T316).
 * @see convex/broker.ts — `getEntitlement` shape (T315; the decision
 *      object's `{ entitled, reason }` literal-union shape).
 * @see specs/prd.md FR-029 — consumer-entitlement gate primitive.
 */
import React, { type CSSProperties, type ReactNode, useId } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { Skeleton } from './Skeleton';

/**
 * The broker's decision shape, mirroring `convex/broker.ts:getEntitlement`'s
 * return type (T315). The `reason` literals carry the PRD §E.4 algorithm
 * step that produced the decision; the gate primitive renders the same
 * disable-and-tooltip wrapper regardless of which deny-reason fired, but
 * the literal union keeps callers honest at the type level.
 */
export interface EntitlementDecision {
  entitled: boolean;
  reason:
    | 'cache_hit'
    | 'ungated'
    | 'no_subscription'
    | 'stale_fail_closed';
}

interface EntitlementGateProps {
  /**
   * The Stripe Entitlement `lookup_key` this affordance gates on (e.g.
   * `"gated_video"`, `"audio_premium"`). Surfaced in the disabled-state
   * tooltip so power-users can cross-reference the subscription plan.
   * An empty string indicates ungated content; the gate renders the
   * children verbatim in that case (PRD §E.4 step 0 — "ungated"
   * short-circuit; mirrors the broker's behaviour on the server side).
   */
  requiredFeature: string;
  /** The gated affordance. Stays in the DOM in every state except RESOLVING. */
  children: ReactNode;
  /**
   * The broker's decision for this `(audienceId, requiredFeature)` pair.
   *
   *   - `undefined` — still resolving (Skeleton).
   *   - `null`      — broker decision unreachable; FAIL CLOSED (denied).
   *   - object      — definitive answer; gate renders enabled OR disabled
   *                   per `entitled`.
   *
   * The frontend has no PUBLIC broker query landed yet (`getEntitlement`
   * is `internalQuery`); until a public wrapper lands, callers pass
   * `null` to render the honest fail-closed denied state. Once a public
   * wrapper lands, callers will pass the result of
   * `useQuery(api.broker.getEntitlementForAudience, { requiredFeature })`
   * directly.
   */
  decision?: EntitlementDecision | null;
  /**
   * Override the default deny-state tooltip. Defaults to a Spanish
   * operator-language string naming the required feature (matches the
   * v.3 prototype's shipping language; mirrors `AccessGate.tsx:68`).
   */
  tooltipText?: string;
  /**
   * Optional fallback to substitute for the wrapped children in the
   * DENIED state. Provided for parity with `AccessGate`'s
   * backwards-compat shape; most call sites omit it — the default
   * disable-and-tooltip wrapper is the canonical UX.
   */
  fallback?: ReactNode;
}

/**
 * Local-storage key the HTTP boundary uses to persist the consumer
 * session `audienceId` (the issuer/validator helpers in
 * `convex/consumerIdentity.ts` produce/verify the token; this key holds
 * the resolved audienceId on the client between page loads).
 *
 * The key is read-only from the gate's perspective — the gate NEVER
 * writes it (that's the HTTP boundary's job at the
 * `/consumer/session` endpoint that runs `issueConsumerSessionToken`).
 */
const AUDIENCE_ID_STORAGE_KEY = 'circa_audience_id';

/**
 * Read the audienceId persisted by the consumer-session HTTP boundary,
 * or `null` when no session exists yet. Returns `null` in SSR/jsdom
 * contexts where `window` is absent (defensive — the gate then renders
 * the unauthenticated DENIED state, never throws).
 */
function readAudienceIdFromStorage(): string | null {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }
  try {
    const v = window.localStorage.getItem(AUDIENCE_ID_STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    // localStorage can throw under strict cookie / privacy modes; the
    // gate degrades to the unauthenticated DENIED state rather than
    // crashing the surface (honest-state contract).
    return null;
  }
}

/**
 * Honest-state tooltip copy. Operator-language (Spanish — the v.3
 * prototype's shipping language; mirrors `AccessGate.tsx:tooltipFor`).
 * Names the required feature verbatim so power-users can cross-reference
 * the subscription plan / monetization page.
 */
function tooltipFor(feature: string): string {
  return `Necesitas la suscripción "${feature}" para acceder a este contenido.`;
}

export function EntitlementGate({
  requiredFeature,
  children,
  decision,
  tooltipText,
  fallback,
}: EntitlementGateProps): React.ReactElement {
  const tooltipId = useId();

  // Read the consumer session (audienceId) the HTTP boundary persisted.
  // The audienceId is the opaque app-issued key (PRD §E.6); the server
  // resolver (`resolveConsumerSession`) returns `{ audienceId }` ONLY
  // — no operator field crosses the FR-028 / §10 boundary.
  const audienceId = readAudienceIdFromStorage();

  // Server-side session validity check. The HTTP boundary issued the
  // token; this query confirms the audienceMembers row still exists.
  // Skipping the query when no audienceId is present keeps the gate's
  // network footprint zero for unauthenticated consumers.
  //
  // Convex `useQuery` returns:
  //   - `undefined` while loading (RESOLVING).
  //   - `null` when the resolver returned null (unknown audienceId).
  //   - `{ audienceId }` on success.
  // The query argument is `{ audienceId }`; `'skip'` short-circuits to
  // a synchronous `undefined` (Convex's documented skip semantic).
  const consumerSession = useQuery(
    api.consumerIdentity.resolveConsumerSession,
    audienceId !== null ? { audienceId } : 'skip',
  );

  // STATE 0 — ungated content. Mirrors the broker's `getEntitlement`
  // step 0 short-circuit (`convex/broker.ts:112-115`): an empty
  // `requiredFeature` means the caller has stated the content carries
  // no gate. Render the children verbatim without any consultation of
  // session or decision.
  if (requiredFeature === '') {
    return <>{children}</>;
  }

  // STATE 1 — RESOLVING. Either the consumer-session resolver is still
  // loading OR the caller has not yet supplied a `decision` prop. The
  // skeleton's shape matches a typical gated-content card (wider/taller
  // than the AccessGate button-shaped skeleton) so the layout reserves
  // realistic space. NEVER render the underlying children here — that
  // would optimistically leak the gated content during the load window.
  //
  // Note: when `audienceId === null` the session query is `'skip'` and
  // returns `undefined` synchronously; that's NOT loading — it's the
  // unauthenticated DENIED branch, handled in STATE 3 below. So the
  // RESOLVING check guards against `consumerSession === undefined`
  // ONLY when the query was actually issued (audienceId present), or
  // against an undefined decision prop.
  const sessionStillResolving =
    audienceId !== null && consumerSession === undefined;
  if (sessionStillResolving || decision === undefined) {
    return <Skeleton w={240} h={120} r={16} aria-busy="true" />;
  }

  // Allowance: we require BOTH a real consumer session (a known
  // `audienceId` resolved by the server) AND a definitive broker
  // decision that says `entitled: true`. Any other shape collapses
  // to `false` (FAIL CLOSED — INV-4).
  const hasSession =
    audienceId !== null && consumerSession !== null;
  const allowed =
    hasSession && decision !== null && decision.entitled === true;

  if (allowed) {
    // STATE 2 — ENTITLED. Render children verbatim; no wrapper
    // attributes that would affect their interactive surface. The
    // server-is-real-gate invariant still applies: the gated media's
    // own signer (CloudFront video signer / private-feed token /
    // audio enclosure signer) is the authoritative deny in production.
    return <>{children}</>;
  }

  // STATE 3 — DENIED. Allow callers to substitute a fallback (parity
  // with `AccessGate`'s opt-in shape); otherwise render the canonical
  // disable-and-tooltip wrapper. The wrapper KEEPS the children in
  // the DOM so the audience sees the affordance exists — this is the
  // FR-029 "disable-and-tooltip" half of the contract.
  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  const tip = tooltipText ?? tooltipFor(requiredFeature);
  const wrapperStyle: CSSProperties = {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
    display: 'inline-flex',
  };

  return (
    <span
      title={tip}
      aria-label={tip}
      aria-describedby={tooltipId}
      role="group"
      style={wrapperStyle}
      data-circa-entitlement-gate="disabled"
      data-circa-required-feature={requiredFeature}
    >
      {/* off-screen description for assistive tech — same shape as
          `AccessGate.tsx:122-137` to keep a11y parity across the two
          gate primitives. */}
      <span
        id={tooltipId}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {tip}
      </span>
      {children}
    </span>
  );
}

export default EntitlementGate;
